/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C } from "../shared/globals.js";

function SharesAccumulatedChart({ entries }) {
  const wrapRef = useRef(null);
  const pathRef = useRef(null);
  const areaRef = useRef(null);
  const [dims, setDims] = useState({ w: 600, h: 180 });
  const [tooltip, setTooltip] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => {
    const obs = new ResizeObserver(es => setDims({ w: Math.max(es[0].contentRect.width, 200), h: 180 }));
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims(d => ({ ...d, w: width }));
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    [pathRef, areaRef].forEach((ref, i) => {
      const el = ref.current; if (!el) return;
      const len = el.getTotalLength?.() || 2000;
      el.style.transition = "none";
      el.style.strokeDasharray = `${len}`;
      el.style.strokeDashoffset = `${len}`;
      el.style.opacity = "0";
      requestAnimationFrame(() => setTimeout(() => {
        el.style.transition = `stroke-dashoffset ${1200 + i*200}ms cubic-bezier(0.4,0,0.2,1), opacity 200ms ease`;
        el.style.strokeDashoffset = "0";
        el.style.opacity = "1";
      }, 80));
    });
  }, [entries.length, dims]);

  if (!entries.length) return null;

  const sorted = [...entries].sort((a,b) => a.date.localeCompare(b.date));
  let cum = 0;
  const points = sorted.map(e => { cum += e.shares; return { date: e.date, shares: cum }; });
  if (points.length < 2) return null;

  const PAD = { t:20, r:20, b:30, l:52 };
  const W = dims.w, H = dims.h;
  const gW = W - PAD.l - PAD.r, gH = H - PAD.t - PAD.b;

  const maxS = Math.max(...points.map(p => p.shares)) * 1.08;
  const xS = i => PAD.l + (i / Math.max(points.length-1,1)) * gW;
  const yS = v => PAD.t + gH - (v / maxS) * gH;

  // Line path
  let d = `M ${xS(0)} ${yS(points[0].shares)}`;
  for (let i = 1; i < points.length; i++) {
    const cpx = (xS(i-1) + xS(i)) / 2;
    d += ` C ${cpx} ${yS(points[i-1].shares)}, ${cpx} ${yS(points[i].shares)}, ${xS(i)} ${yS(points[i].shares)}`;
  }
  // Area path
  const areaD = d + ` L ${xS(points.length-1)} ${PAD.t+gH} L ${xS(0)} ${PAD.t+gH} Z`;

  // Milestones
  const MILESTONES = [50, 100, 200, 250, 500].filter(m => m <= maxS);
  const milestones = MILESTONES.map(m => {
    const idx = points.findIndex(p => p.shares >= m);
    if (idx < 0) return null;
    return { m, x: xS(idx), y: yS(m) };
  }).filter(Boolean);

  // Y ticks
  const yTicks = Array.from({length:4}, (_,i) => Math.round((maxS/3)*i));
  const xLabels = [0, Math.floor((points.length-1)/2), points.length-1];
  const fmtDate = s => { const [y,m,d2]=s.split("-"); return `${d2}.${m}.${y.slice(2)}`; };

  const onMove = (e) => {
    const rect = wrapRef.current?.querySelector("svg")?.getBoundingClientRect();
    if (!rect) return;
    const idx = Math.max(0, Math.min(points.length-1, Math.round(((e.clientX - rect.left - PAD.l) / gW) * (points.length-1))));
    clearTimeout(hideTimer.current);
    setTooltip({ x: xS(idx), y: yS(points[idx].shares), pt: points[idx] });
    requestAnimationFrame(() => setTooltipVisible(true));
  };

  return (
    <div ref={wrapRef} style={{ width:"100%", position:"relative" }}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:"block", overflow:"visible" }}
        onMouseMove={onMove}
        onMouseLeave={() => { setTooltipVisible(false); hideTimer.current = setTimeout(() => setTooltip(null), 400); }}>

        <defs>
          <linearGradient id="sharesAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.green} stopOpacity="0.25"/>
            <stop offset="100%" stopColor={C.green} stopOpacity="0.02"/>
          </linearGradient>
        </defs>

        {/* Y ticks */}
        {yTicks.map((v,i) => (
          <g key={i}>
            <line x1={PAD.l} y1={yS(v)} x2={W-PAD.r} y2={yS(v)} stroke="var(--border)" strokeWidth="1"/>
            <text x={PAD.l-6} y={yS(v)+4} textAnchor="end" fill="var(--text5)"
              fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">{v.toFixed(0)}</text>
          </g>
        ))}

        {/* X labels */}
        {xLabels.map(idx => (
          <text key={idx} x={xS(idx)} y={H-6} textAnchor="middle" fill="var(--text5)"
            fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
            {fmtDate(points[idx].date)}
          </text>
        ))}

        {/* Area fill */}
        <path ref={areaRef} d={areaD} fill="url(#sharesAreaGrad)" stroke="none" style={{opacity:0}}/>

        {/* Line */}
        <path ref={pathRef} d={d} fill="none"
          stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{opacity:0}}/>

        {/* Milestones */}
        {milestones.map(({m, x, y}) => (
          <g key={m}>
            <line x1={x} y1={PAD.t} x2={x} y2={PAD.t+gH} stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3"/>
            <circle cx={x} cy={y} r="3.5" fill={C.green}/>
            <rect x={x+5} y={y-18} width={m>=100?32:26} height={14} rx="3" fill="var(--bg2)" stroke="var(--border)"/>
            <text x={x+8} y={y-8} fill={C.green} fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">{m}</text>
          </g>
        ))}

        {/* Crosshair + dot */}
        {tooltip && (
          <g>
            <line x1={tooltip.x} y1={PAD.t} x2={tooltip.x} y2={PAD.t+gH}
              stroke="var(--border2)" strokeWidth="1" strokeDasharray="4 3"/>
            <circle cx={tooltip.x} cy={tooltip.y} r="4" fill={C.green} stroke="var(--bg2)" strokeWidth="1.5"/>
          </g>
        )}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position:"absolute",
          left: Math.min(Math.max(tooltip.x - 70, 0), dims.w - 150),
          top: tooltip.y - 60,
          background:"var(--bg2)", border:`1px solid ${C.green}`,
          borderRadius:8, padding:"8px 14px",
          pointerEvents:"none", fontFamily:"Montserrat,sans-serif",
          zIndex:10, opacity: tooltipVisible ? 1 : 0,
          transition: tooltipVisible ? "opacity 0.2s ease" : "opacity 0.4s ease",
        }}>
          <div style={{ fontSize:9, color:"var(--text4)", letterSpacing:"0.2em", marginBottom:5 }}>
            {fmtDate(tooltip.pt.date)}
          </div>
          <div style={{ fontSize:18, fontWeight:900, color:C.green, letterSpacing:"-0.02em" }}>
            {tooltip.pt.shares.toFixed(4)}
          </div>
          <div style={{ fontSize:8, color:"var(--text4)", letterSpacing:"0.1em" }}>SHARES</div>
        </div>
      )}
    </div>
  );
}


export default SharesAccumulatedChart;
